import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { formatMoney } from '../utils/global';

type ProductCategoryData = {
  category: string;
  totalRevenue: number;
  totalProfit: number;
  totalQuantity: number;
  transactionCount: number;
  averagePrice: number;
  profitMargin: number;
};

type ProductCategoryAnalysisProps = {
  data: ProductCategoryData[];
  chartType?: 'bar' | 'pie';
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];

export default function ProductCategoryAnalysis({ 
  data, 
  chartType = 'bar' 
}: ProductCategoryAnalysisProps) {
  const theme = useTheme();

  const chartData = data.map(item => ({
    name: item.category,
    revenue: item.totalRevenue / 100,
    profit: item.totalProfit / 100,
    quantity: item.totalQuantity,
    margin: item.profitMargin
  }));

  const pieData = data.map(item => ({
    name: item.category,
    value: item.totalRevenue / 100
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 2,
            boxShadow: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="body2">
                {entry.dataKey === 'revenue' && 'Ciro: '}
                {entry.dataKey === 'profit' && 'Kar: '}
                {entry.dataKey === 'quantity' && 'Miktar: '}
                {entry.dataKey === 'margin' && 'Kar Marjı: '}
                {entry.dataKey === 'margin' 
                  ? `${entry.value.toFixed(2)}%`
                  : entry.dataKey === 'quantity'
                  ? `${entry.value} adet`
                  : formatMoney(entry.value.toString())
                }
                {entry.dataKey !== 'margin' && entry.dataKey !== 'quantity' && ' TL'}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={(entry: any) => `${entry.name} ${(entry.percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="revenue" fill="#0088FE" name="Ciro" />
          <Bar dataKey="profit" fill="#00C49F" name="Kar" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const getTotalStats = () => {
    const totalRevenue = data.reduce((sum, item) => sum + item.totalRevenue, 0);
    const totalProfit = data.reduce((sum, item) => sum + item.totalProfit, 0);
    const totalQuantity = data.reduce((sum, item) => sum + item.totalQuantity, 0);
    const totalTransactions = data.reduce((sum, item) => sum + item.transactionCount, 0);
    const averageProfitMargin = data.length > 0 
      ? data.reduce((sum, item) => sum + item.profitMargin, 0) / data.length 
      : 0;

    return { totalRevenue, totalProfit, totalQuantity, totalTransactions, averageProfitMargin };
  };

  const stats = getTotalStats();

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Ürün Kategorisi Analizi
      </Typography>

      {/* Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {renderChart()}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {formatMoney((stats.totalRevenue / 100).toString())} TL
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Ciro
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
              {formatMoney((stats.totalProfit / 100).toString())} TL
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Kar
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
              {stats.totalQuantity}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Miktar
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
              {stats.totalTransactions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam İşlem
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
              {stats.averageProfitMargin.toFixed(2)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ort. Kar Marjı
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Detailed Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell sx={{ fontWeight: 600 }}>Kategori</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam Ciro</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam Kar</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Miktar</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>İşlem Sayısı</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Ort. Fiyat</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Kar Marjı</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {item.category}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatMoney((item.totalRevenue / 100).toString())} TL
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                    {formatMoney((item.totalProfit / 100).toString())} TL
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {item.totalQuantity} adet
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {item.transactionCount}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatMoney((item.averagePrice / 100).toString())} TL
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${item.profitMargin.toFixed(2)}%`}
                    sx={{
                      backgroundColor: item.profitMargin > 15 ? theme.palette.success.main : 
                                      item.profitMargin > 10 ? theme.palette.warning.main : 
                                      theme.palette.error.main,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
